import { useCallback, useEffect, useState } from 'react'
import { useParams, useSearchParams } from "react-router-dom"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faExclamationCircle } from '@fortawesome/duotone-regular-svg-icons'
import dayjs from 'dayjs'

import { message } from 'antd'

import Loading from 'components/Loading'

import { discectError, formatMoney, getSuretyLogo } from 'lib/utils'
import api from 'lib/api'

import styles from './styles.module.scss'

const Receipt = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [power, setPower] = useState(null)

  const { powerId } = useParams()
  const [searchParams] = useSearchParams();

  const key = searchParams.get("key");

  const [messageApi, contextHolder] = message.useMessage()

  const _fetchReport = useCallback(async () => {
    setIsLoading(true)
    try {
      const { power: incomingPower } = await api.inventory.verifyPowerDetails(powerId, { key })
      setPower(incomingPower)
    } catch(e) {
      console.error(e)
      messageApi.open({
        type: 'error',
        content: discectError(e)
      })
    }
    setIsLoading(false)
  }, [powerId, key, messageApi])

  useEffect(() => {
    if (powerId && !power) _fetchReport()
  }, [_fetchReport, powerId, power, messageApi])

  return (
    <div className={styles.receipt__container}>
      <div className={styles.receipt}>
        <header>
          {getSuretyLogo(power?.suretyId, power?.suretyName)}
          {/* <p>You can upload power proofs to your report using the tools below. Ensure all required files are uploaded before submitting your report.</p> */}
        </header>
        {power?.addressInfo && (
          <div className={styles.receipt__addressBlock}>
            <h4>{power?.addressInfo?.name}</h4>
            <p>{power?.addressInfo?.address1} {power?.addressInfo?.address2}<br />{power?.addressInfo?.city}, {power?.addressInfo?.state} {power?.addressInfo?.zip}<br />{power?.addressInfo?.phone}<br />{power?.addressInfo?.email}</p>
          </div>
        )}
        {power && (
          <>
            <div className={styles.validityBlock__container}>
              {power?.isValid && (
                <div className={styles.validityBlock}>
                  <div className={styles.icon}>              
                    <FontAwesomeIcon icon={faCheckCircle} />
                  </div>
                  <div className={styles.validityBlock__details}>
                    <h4>This Power is Valid!</h4>
                    <p>This printable security key matches</p>
                  </div>
                </div>
              )}
              {!power?.isValid && (
                <div className={`${styles.validityBlock} ${styles.validityBlock__invalid}`}>
                  <div className={styles.icon}>              
                    <FontAwesomeIcon icon={faExclamationCircle} />
                  </div>
                  {power.errorCode === 'HASH_MISMATCH' && (
                    <div className={styles.validityBlock__details}>
                      <h4>This Power is INVALID!</h4>
                      <p>The security key <strong>DOES NOT MATCH</strong> what we have on file.</p>
                    </div>
                  )}
                  {power.errorCode === 'POWER_IS_VOID' && (
                    <div className={styles.validityBlock__details}>
                      <h4>This Power is VOID!</h4>
                      <p>This power is marked as Void and is Invalid.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className={styles.validityBlock__caseDetails}>
              <h4>Power #: {power.powerNumber}</h4>
              <p><strong>Date Issued</strong>: {dayjs(power.dateIssued.split('T').shift()).format('MMM. D, YYYY')}</p>
              <p><strong>Date Expires</strong>: {dayjs(power.dateExpires.split('T').shift()).format('MMM. D, YYYY')}</p>
              {power?.is_digital_power && (
                <>
                  <p><strong>Case/Docket #</strong>: {power.caseNumber || 'N/A'}</p>
                  <p><strong>Bond Amount</strong>: {formatMoney(power.bondAmount)}</p>
                  <p><strong>Defendant Name</strong>: {power.defendantName || 'N/A'}</p>
                  <p><strong>County</strong>: {power.county ? `${power.county} County` : 'N/A'}</p>
                  <p><strong>City/State</strong>: {power.city || 'N/A'}, {power.state || 'N/A'}</p>
                  <p><strong>Court Name</strong>: {power.court || 'N/A'}</p>
                  <p><strong>Charge</strong>: {power.charge || 'N/A'}</p>
                </>
              )}
            </div>
            <div className={styles.validityBlock__security}>
              <h5 className="rad">Security Features</h5>
              <p>Our documents are printed on VoidSecure® security paper, which includes advanced protections against tampering and forgery. Look for the built-in features such as the “VOID” warning that appears on photocopies, invisible fluorescent fibers under UV light, and the coin-rub watermark on the back of the page. Additional safeguards like chemical stain detection, TonerSecure™ anti-tamper technology, and artificial watermarks ensure authenticity. Always check for these features to verify that the document is genuine.</p>
              <p><strong>To quickly verify document security:</strong></p>
              <p>Confirm the presence of the watermark on the back of the document. Ensure “VOID” does not appear on the page, and use a UV light to detect the embedded fluorescent fibers.</p>
            </div>
          </>
        )}
      </div>
      <div className={styles.poweredBy}>
        <h5 className="rad">Powered by</h5>
        <a href="https://simplybail.com" target="_blank" rel="noreferrer">
          <img src="/images/SimplyBail_logo_long.png" alt="SimplyBail" />
        </a>
      </div>
      {contextHolder}
      {isLoading && <Loading />}
    </div>
  ) 
}

export default Receipt